/**
 * The drawn elements: plot, chart, diagram, timeline, number line, flow.
 *
 * All SVG, all deterministic, all built from the element's own spec. Nothing here fetches,
 * measures or animates — a slide has to look the same on the projector as it did when the
 * teacher approved it.
 *
 * Every one carries `role="img"` and the element's own `alt`, so what a screen reader hears
 * comes from the same source as what is drawn rather than being written beside it.
 */

import type { SlideElement, DiagramShape } from './types';
import { SERIES, CHART_INK, MARK } from './design';

/*
 * Projection sizing. A chart drawn for a report disappears in a room: 11px axis labels are
 * unreadable at eight metres and a 1px gridline is gone entirely under a projector lamp. The
 * type here is roughly double a report's and the marks are four times the weight.
 */
const AXIS = CHART_INK.axis;
const GRID = CHART_INK.grid;
const INK = CHART_INK.label;
const SERIES_COLORS = SERIES;
const FS = { tick: 19, axis: 22, series: 20 };

function niceTicks(lo: number, hi: number, count = 5): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const first = Math.ceil(lo / step) * step;
  const out: number[] = [];
  for (let v = first; v <= hi + step / 1e6; v += step) out.push(Number(v.toFixed(10)));
  return out;
}

const fmt = (n: number) => (Math.abs(n) >= 1000 || (n !== 0 && Math.abs(n) < 0.01) ? n.toExponential(1) : String(Number(n.toFixed(2))));

/* ------------------------------------------------------------------------------- plot */

export function PlotView({ el }: { el: Extract<SlideElement, { kind: 'plot' }> }) {
  const W = 900, H = 500, PAD = { l: 92, r: 132, t: 26, b: 74 };
  const series = el.samples ?? [];
  const ys = series.flatMap((s) => s.ys.filter((y): y is number => y !== null));

  if (!ys.length) {
    return <p style={{ color: 'var(--c-ink-3)', fontSize: '.9rem' }}>This plot has no drawable points.</p>;
  }

  const [x0, x1] = el.domain;
  let y0 = Math.min(...ys), y1 = Math.max(...ys);
  if (y0 === y1) { y0 -= 1; y1 += 1; }
  // A little headroom, and always show y = 0 when the data straddles it.
  const padY = (y1 - y0) * 0.08;
  y0 -= padY; y1 += padY;

  const sx = (x: number) => PAD.l + ((x - x0) / (x1 - x0)) * (W - PAD.l - PAD.r);
  const sy = (y: number) => H - PAD.b - ((y - y0) / (y1 - y0)) * (H - PAD.t - PAD.b);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={el.alt} style={{ maxHeight: '52vh' }}>
      {/* Four gridlines, not ten. The grid orients; it is not the thing being read. */}
      {niceTicks(y0, y1, 4).map((t) => (
        <g key={`y${t}`}>
          <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} stroke={GRID} strokeWidth={MARK.gridline} />
          <text x={PAD.l - 14} y={sy(t) + 7} textAnchor="end" fontSize={FS.tick} fill={AXIS}>{fmt(t)}</text>
        </g>
      ))}
      {niceTicks(x0, x1, 4).map((t) => (
        <text key={`x${t}`} x={sx(t)} y={H - PAD.b + 30} textAnchor="middle" fontSize={FS.tick} fill={AXIS}>{fmt(t)}</text>
      ))}
      <line x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} stroke={AXIS} strokeWidth={MARK.axis} />
      <line x1={PAD.l} x2={PAD.l} y1={PAD.t} y2={H - PAD.b} stroke={AXIS} strokeWidth={MARK.axis} />

      {series.map((s, i) => {
        // A null sample is a break in the curve, so the pen lifts rather than drawing across it.
        const runs: string[] = [];
        let current = '';
        for (let k = 0; k < s.xs.length; k++) {
          const y = s.ys[k];
          if (y === null || y === undefined || y < y0 || y > y1) {
            if (current) runs.push(current);
            current = '';
            continue;
          }
          current += `${current ? 'L' : 'M'}${sx(s.xs[k]!).toFixed(1)},${sy(y).toFixed(1)}`;
        }
        if (current) runs.push(current);
        return runs.map((d, k) => (
          <path key={`${i}-${k}`} d={d} fill="none" stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={MARK.line} strokeLinecap="round" />
        ));
      })}

      {el.xLabel ? <text x={(PAD.l + W - PAD.r) / 2} y={H - 16} textAnchor="middle" fontSize={FS.axis} fill={INK}>{el.xLabel}</text> : null}
      {el.yLabel ? <text x={24} y={(PAD.t + H - PAD.b) / 2} textAnchor="middle" fontSize={FS.axis} fill={INK} transform={`rotate(-90 24 ${(PAD.t + H - PAD.b) / 2})`}>{el.yLabel}</text> : null}

      {/*
        The series is named at the end of its own line rather than in a legend box. A legend
        makes a room look away from the chart, match a swatch to a word, and look back; a
        label on the line is read where the eye already is.
      */}
      {series.map((s, i) => {
        const last = [...s.ys.keys()].reverse().find((k) => {
          const y = s.ys[k];
          return y !== null && y !== undefined && y >= y0 && y <= y1;
        });
        if (last === undefined) return null;
        return (
          <text
            key={`n${i}`}
            x={Math.min(sx(s.xs[last]!) + 12, W - 8)}
            y={sy(s.ys[last] as number) + 7}
            fontSize={FS.series}
            fontWeight={650}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
          >
            {s.label}
          </text>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------------------ chart */

export function ChartView({ el }: { el: Extract<SlideElement, { kind: 'chart' }> }) {
  const W = 900, H = 500, PAD = { l: 92, r: 40, t: 26, b: 96 };
  const all = el.series.flatMap((s) => s.values);
  if (!all.length) return null;
  let y0 = Math.min(0, ...all), y1 = Math.max(0, ...all);
  if (y0 === y1) y1 = y0 + 1;
  const pad = (y1 - y0) * 0.08;
  y1 += pad;
  if (y0 < 0) y0 -= pad;

  const plotW = W - PAD.l - PAD.r;
  const sy = (y: number) => H - PAD.b - ((y - y0) / (y1 - y0)) * (H - PAD.t - PAD.b);
  const band = plotW / el.categories.length;
  const barW = Math.min(56, (band * 0.7) / el.series.length);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={el.alt} style={{ maxHeight: '52vh' }}>
      {niceTicks(y0, y1, 4).map((t) => (
        <g key={t}>
          <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} stroke={GRID} strokeWidth={MARK.gridline} />
          <text x={PAD.l - 14} y={sy(t) + 7} textAnchor="end" fontSize={FS.tick} fill={AXIS}>{fmt(t)}</text>
        </g>
      ))}
      <line x1={PAD.l} x2={W - PAD.r} y1={sy(0)} y2={sy(0)} stroke={AXIS} strokeWidth={MARK.axis} />

      {el.categories.map((c, ci) => (
        <text key={c + ci} x={PAD.l + band * (ci + 0.5)} y={H - PAD.b + 32} textAnchor="middle" fontSize={FS.tick} fill={AXIS}>{c}</text>
      ))}

      {el.series.map((s, si) =>
        s.values.map((v, ci) => {
          const cx = PAD.l + band * (ci + 0.5);
          const color = SERIES_COLORS[si % SERIES_COLORS.length];
          if (el.chart === 'bar') {
            const x = cx - (barW * el.series.length) / 2 + si * barW;
            const top = Math.min(sy(v), sy(0));
            // 2px of surface between adjacent bars, and a 4px rounded end on the data side.
            return <rect className="slide-bar" key={`${si}-${ci}`} x={x} y={top} width={barW - 4} height={Math.abs(sy(v) - sy(0))} fill={color} rx={4} />;
          }
          if (el.chart === 'scatter') {
            return <circle key={`${si}-${ci}`} cx={cx} cy={sy(v)} r={MARK.point} fill={color} stroke="var(--c-bg, #fff)" strokeWidth={2} />;
          }
          return null;
        }),
      )}

      {el.chart === 'line'
        ? el.series.map((s, si) => (
            <path
              key={si}
              d={s.values.map((v, ci) => `${ci ? 'L' : 'M'}${PAD.l + band * (ci + 0.5)},${sy(v)}`).join('')}
              fill="none"
              stroke={SERIES_COLORS[si % SERIES_COLORS.length]}
              strokeWidth={MARK.line}
              strokeLinecap="round"
            />
          ))
        : null}

      {el.series.length > 1 ? el.series.map((s, i) => (
        <g key={`l${i}`} transform={`translate(${PAD.l + i * 210}, ${H - 18})`}>
          <rect className="slide-legend" width={20} height={20} y={-16} fill={SERIES_COLORS[i % SERIES_COLORS.length]} rx={4} />
          {/* The label wears text ink, never the series colour — the swatch carries identity. */}
          <text x={28} fontSize={FS.series} fill={INK}>{s.label}</text>
        </g>
      )) : null}
    </svg>
  );
}

/* ---------------------------------------------------------------------------- diagram */

export function DiagramView({ el }: { el: Extract<SlideElement, { kind: 'diagram' }> }) {
  /*
   * A line's label goes 30% along it and a little to one side, not at the midpoint. Two lines
   * that cross — supply and demand, most obviously — have midpoints in nearly the same place,
   * and midpoint labels print on top of each other.
   */
  const labelAt = (x1: number, y1: number, x2: number, y2: number) => {
    const t = 0.3;
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    return { x: x1 + dx * t - (dy / len) * 12, y: y1 + dy * t + (dx / len) * 12 };
  };

  const poly = (xs: number[], ys: number[]) => xs.map((x, k) => `${k ? 'L' : 'M'}${x},${ys[k]}`).join(' ');

  const draw = (sh: DiagramShape, i: number) => {
    switch (sh.s) {
      case 'curve': {
        const last = sh.xs.length - 1;
        return (
          <g key={i}>
            <path
              d={poly(sh.xs, sh.ys)}
              fill="none"
              stroke={sh.accent ? 'var(--c-accent-2, #0e8f6f)' : 'var(--c-brand, #5b46d6)'}
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={sh.dashed ? '10 7' : undefined}
            />
            {sh.label && last >= 0 ? (
              <text x={sh.xs[last]! + 8} y={sh.ys[last]! - 6} fontSize={16} fontWeight={650}
                fill={sh.accent ? 'var(--c-accent-2, #0e8f6f)' : 'var(--c-brand, #5b46d6)'}>
                {sh.label}
              </text>
            ) : null}
          </g>
        );
      }
      case 'area': {
        // A shaded region — a surplus, a shortage, the area under a curve. Kept faint, and
        // outlined, so it reads as a region rather than as a solid object.
        const m = Math.floor(sh.xs.length / 2);
        return (
          <g key={i}>
            <path d={`${poly(sh.xs, sh.ys)} Z`} fill="var(--c-brand, #5b46d6)" fillOpacity={0.13} stroke="none" />
            {sh.label && sh.xs.length ? (
              <text x={sh.xs[m]} y={sh.ys[m]} fontSize={15} fontWeight={620} textAnchor="middle" fill={AXIS}>{sh.label}</text>
            ) : null}
          </g>
        );
      }
      case 'arrow':
        return (
          <g key={i}>
            <line x1={sh.x1} y1={sh.y1} x2={sh.x2} y2={sh.y2} stroke={INK} strokeWidth={2} markerEnd="url(#slide-arrow)" />
            {sh.label ? (() => { const p = labelAt(sh.x1, sh.y1, sh.x2, sh.y2); return <text x={p.x} y={p.y} fontSize={14} fill={INK}>{sh.label}</text>; })() : null}
          </g>
        );
      case 'line':
        return (
          <g key={i}>
            <line x1={sh.x1} y1={sh.y1} x2={sh.x2} y2={sh.y2} stroke={AXIS} strokeWidth={1.5} strokeDasharray={sh.dashed ? '6 5' : undefined} />
            {sh.label ? (() => { const p = labelAt(sh.x1, sh.y1, sh.x2, sh.y2); return <text x={p.x} y={p.y} fontSize={13} fill={AXIS}>{sh.label}</text>; })() : null}
          </g>
        );
      case 'circle':
        return (
          <g key={i}>
            <circle cx={sh.cx} cy={sh.cy} r={sh.r} fill={sh.filled ? 'var(--c-brand)' : 'none'} stroke={INK} strokeWidth={2} />
            {sh.label ? <text x={sh.cx} y={sh.cy + 5} textAnchor="middle" fontSize={13} fill={sh.filled ? '#fff' : INK}>{sh.label}</text> : null}
          </g>
        );
      case 'rect':
        return (
          <g key={i}>
            <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} fill="none" stroke={INK} strokeWidth={2} rx={3} />
            {sh.label ? <text x={sh.x + sh.w / 2} y={sh.y + sh.h / 2 + 5} textAnchor="middle" fontSize={13} fill={INK}>{sh.label}</text> : null}
          </g>
        );
      case 'text':
        return <text key={i} x={sh.x} y={sh.y} fontSize={14} fill={INK}>{sh.text}</text>;
    }
  };

  return (
    <svg viewBox={`0 0 ${el.width} ${el.height}`} width="100%" role="img" aria-label={el.alt} style={{ maxHeight: '52vh' }}>
      <defs>
        <marker id="slide-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={INK} />
        </marker>
      </defs>
      {el.shapes.map(draw)}
    </svg>
  );
}

/* --------------------------------------------------------------------------- timeline */

export function TimelineView({ el }: { el: Extract<SlideElement, { kind: 'timeline' }> }) {
  const W = 760, H = 60 + el.events.length * 46;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={el.alt} style={{ maxHeight: '54vh' }}>
      <line x1={90} x2={90} y1={20} y2={H - 20} stroke={GRID} strokeWidth={3} />
      {el.events.map((e, i) => {
        const y = 40 + i * 46;
        return (
          <g key={i}>
            <text x={78} y={y + 5} textAnchor="end" fontSize={14} fontWeight={600} fill={AXIS}>{e.date}</text>
            <circle cx={90} cy={y} r={6} fill="var(--c-brand)" />
            <text x={110} y={y + 5} fontSize={15} fill={INK}>{e.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------------ number line */

export function NumberLineView({ el }: { el: Extract<SlideElement, { kind: 'numberLine' }> }) {
  const W = 760, H = 120, PAD = 50;
  const sx = (v: number) => PAD + ((v - el.min) / (el.max - el.min)) * (W - PAD * 2);
  const step = el.step && el.step > 0 ? el.step : (el.max - el.min) / 10;
  const ticks: number[] = [];
  for (let v = el.min; v <= el.max + step / 1e6; v += step) ticks.push(Number(v.toFixed(6)));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={el.alt}>
      <line x1={PAD - 18} x2={W - PAD + 18} y1={60} y2={60} stroke={INK} strokeWidth={2} markerEnd="url(#nl-arrow)" markerStart="url(#nl-arrow)" />
      <defs>
        <marker id="nl-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={INK} />
        </marker>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={sx(t)} x2={sx(t)} y1={53} y2={67} stroke={AXIS} strokeWidth={1.5} />
          <text x={sx(t)} y={86} textAnchor="middle" fontSize={13} fill={AXIS}>{fmt(t)}</text>
        </g>
      ))}
      {el.marks.map((m, i) => (
        <g key={i}>
          <circle cx={sx(m.at)} cy={60} r={7} fill="var(--c-brand)" />
          {m.label ? <text x={sx(m.at)} y={38} textAnchor="middle" fontSize={14} fontWeight={600} fill={INK}>{m.label}</text> : null}
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------------------- flow */

export function FlowView({ el }: { el: Extract<SlideElement, { kind: 'flow' }> }) {
  const n = el.steps.length;
  const boxW = 168, boxH = 62, gap = 46;
  const perRow = Math.min(3, n);
  const rows = Math.ceil(n / perRow);
  const W = perRow * boxW + (perRow - 1) * gap + 40;
  const H = rows * boxH + (rows - 1) * gap + 40;
  const at = (i: number) => ({
    x: 20 + (i % perRow) * (boxW + gap),
    y: 20 + Math.floor(i / perRow) * (boxH + gap),
  });
  const index = new Map(el.steps.map((s, i) => [s.id, i]));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={el.alt} style={{ maxHeight: '52vh' }}>
      <defs>
        <marker id="flow-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={AXIS} />
        </marker>
      </defs>
      {el.edges.map((e, i) => {
        const a = index.get(e.from), b = index.get(e.to);
        if (a === undefined || b === undefined) return null;
        const pa = at(a), pb = at(b);
        const from = { x: pa.x + boxW / 2, y: pa.y + boxH / 2 };
        const to = { x: pb.x + boxW / 2, y: pb.y + boxH / 2 };
        return <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={AXIS} strokeWidth={1.6} markerEnd="url(#flow-arrow)" />;
      })}
      {el.steps.map((s, i) => {
        const p = at(i);
        return (
          <g key={s.id}>
            <rect x={p.x} y={p.y} width={boxW} height={boxH} rx={9} fill="var(--c-surface-2, #f6f5f2)" stroke={GRID} />
            <text x={p.x + boxW / 2} y={p.y + boxH / 2 + 5} textAnchor="middle" fontSize={14} fill={INK}>
              {s.label.length > 22 ? `${s.label.slice(0, 21)}…` : s.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
